import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataContext } from '../Provider/dataProvider';
import { fetchWithAuth } from '../utils/authUtils';
import {
  buildRevisitPath,
  formatAlertDateTime,
  formatMedicalDateTime,
  parseMeta,
  resolveAlertAction,
  revisitSummaryFromMeta,
} from '../utils/alertUtils';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const AlertsPage = () => {
  const { data } = useContext(DataContext);
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const loadAlerts = async () => {
    try {
      setLoading(true);
      setError('');
      const query = filter === 'unread' ? '?unread=1&limit=200' : '?limit=200';
      const res = await fetchWithAuth(`${API_BASE}/api/alerts${query}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) throw new Error(json?.message || 'Không thể tải thông báo');
      setAlerts(Array.isArray(json?.data) ? json.data : []);
    } catch (e) {
      setAlerts([]);
      setError(e.message || 'Không thể tải thông báo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAlerts(); /* eslint-disable-next-line */ }, [filter]);

  const markRead = async (recipientId) => {
    try {
      await fetchWithAuth(`${API_BASE}/api/alerts/${recipientId}/read`, { method: 'PATCH' });
    } finally {
      loadAlerts();
    }
  };

  const unreadCount = useMemo(() => alerts.filter((a) => !a.readAt).length, [alerts]);

  const handleOpen = async (alert, meta) => {
    const action = resolveAlertAction(alert, meta, {
      elderlyId: data?.id || data?.profileId,
    });

    if (!alert.readAt) {
      await markRead(alert.recipientId);
    }

    if (action.type === 'revisit') {
      navigate(buildRevisitPath(action.recordId, meta || {}));
      return;
    }
    if (action.type === 'medication') {
      navigate(`/medication-checklist/${action.elderlyId}`);
      return;
    }
    if (action.type === 'relatives') {
      navigate('/my-relatives');
      return;
    }
  };

  const renderMetaDetails = (meta) => {
    if (!meta || meta.kind !== 'revisit') return null;
    const rows = [
      meta.revisitAt ? { label: 'Giờ tái khám', value: formatMedicalDateTime(meta.revisitAt) } : null,
      meta.hospitalName ? { label: 'Bệnh viện', value: meta.hospitalName } : null,
      meta.diagnosis ? { label: 'Chẩn đoán', value: meta.diagnosis } : null,
    ].filter(Boolean);
    if (rows.length === 0) return null;
    return (
      <div style={{ marginTop: 8, display: 'grid', gap: 4 }}>
        {rows.map((row) => (
          <div key={row.label} style={{ fontSize: 13, color: '#475569' }}>
            <strong style={{ color: '#334155' }}>{row.label}:</strong> {row.value}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="container" style={{ padding: '96px 16px', maxWidth: 960, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 style={{ margin: 0, color: 'var(--primary)' }}>Thông báo</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`tamcare-btn ${filter === 'all' ? 'tamcare-btn-primary' : 'tamcare-btn-ghost'}`} onClick={() => setFilter('all')}>Tất cả</button>
          <button className={`tamcare-btn ${filter === 'unread' ? 'tamcare-btn-primary' : 'tamcare-btn-ghost'}`} onClick={() => setFilter('unread')}>Chưa đọc {unreadCount > 0 ? `(${unreadCount})` : ''}</button>
        </div>
      </div>

      {loading && <div className="tamcare-alert">Đang tải...</div>}
      {error && <div className="tamcare-alert tamcare-alert--error">{error}</div>}

      {!loading && !error && alerts.length === 0 && (
        <div className="tamcare-alert">Không có thông báo.</div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {alerts.map((a) => {
          const meta = parseMeta(a.metaJson);
          const action = resolveAlertAction(a, meta, { elderlyId: data?.id || data?.profileId });
          const summary = meta?.kind === 'revisit' ? revisitSummaryFromMeta(meta) : '';
          const canOpen = action.type === 'revisit' || action.type === 'medication' || action.type === 'relatives';

          return (
            <div key={a.recipientId} className="tamcare-noti-item" style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, background: a.readAt ? '#fff' : '#f8fafc' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <strong style={{ color: '#0f172a' }}>{a.title}</strong>
                <small style={{ color: '#64748b' }}>{formatAlertDateTime(a.createdAt)}</small>
              </div>
              <p style={{ margin: '6px 0 8px', color: '#334155' }}>{summary || a.content}</p>
              {renderMetaDetails(meta)}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                {canOpen && (
                  <button className="tamcare-btn-ghost" onClick={() => handleOpen(a, meta)}>
                    {action.type === 'revisit' ? 'Xem lịch tái khám' : 'Mở lịch uống thuốc'}
                  </button>
                )}
                {!a.readAt && (
                  <button className="tamcare-btn-ghost" onClick={() => markRead(a.recipientId)}>Đánh dấu đã đọc</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AlertsPage;
